/**
 * useAppleAuth – Web implementation of Sign In with Apple.
 *
 * Uses Apple's official Sign In with Apple JS SDK (loaded in index.html).
 * Mirrors the mobile AppleAuthServices flow:
 *   - Existing user  → backend returns { message, user: { ...tokens } }  → login + navigate home
 *   - New user       → backend returns { user: { ...no tokens } }         → pre-fill signupStore + navigate to /role-selection
 *
 * Env vars required (see .env.example):
 *   VITE_APPLE_CLIENT_ID    – Apple Services ID (e.g. "com.musicade.web")
 *   VITE_APPLE_REDIRECT_URI – Registered redirect URI in Apple Developer portal
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAppleLoginMutation } from '../api/auth';
import { useAuth } from './useAuth';
import { mapUserFromAuthResponse } from '../contexts/AuthContext';
import { signupStore } from '../utils/signupStore';
import { toast } from '../utils/toast';

// ─── Apple Sign In JS SDK type declarations ───────────────────────────────────

interface AppleAuthConfig {
  clientId: string;
  scope: string;
  redirectURI: string;
  state?: string;
  nonce?: string;
  usePopup?: boolean;
}

interface AppleAuthResponse {
  authorization: {
    id_token: string;
    code: string;
    state?: string;
  };
  /** Only returned on the very first sign-in for a given Apple ID */
  user?: {
    email?: string;
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: AppleAuthConfig) => void;
        signIn: () => Promise<AppleAuthResponse>;
      };
    };
  }
}

// ─── Utility: decode JWT payload to extract `sub` (Apple user identifier) ────

function getSubFromJwt(token: string): string | null {
  try {
    const b64 = token.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? '';
    const json = JSON.parse(atob(b64));
    return (json.sub as string) ?? null;
  } catch {
    return null;
  }
}

// ─── Internal ApiUser shape (matches what mapUserFromAuthResponse accepts) ────

type ApiUser = {
  id?: string | number;
  email?: string;
  username?: string;
  full_name?: string;
  fullName?: string;
  city?: string;
  state?: string;
  country?: string;
  genres?: string[];
  tags?: string[];
  user_types?: unknown[];
  prof_pics?: string;
  profPics?: string;
  profile_incomplete?: boolean;
  needs_profile_update?: boolean;
  role?: 'creator' | 'fan' | 'venue' | null;
  tokens?: { access?: string; refresh?: string };
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAppleAuth() {
  const [isAppleLoading, setIsAppleLoading] = useState(false);
  const appleMutation = useAppleLoginMutation();
  const { login } = useAuth();
  const navigate = useNavigate();

  /** Initialize the Apple Sign In SDK once it's available in the DOM */
  const initApple = useCallback(() => {
    if (!window.AppleID) return;
    window.AppleID.auth.init({
      clientId: import.meta.env.VITE_APPLE_CLIENT_ID ?? '',
      scope: 'name email',
      redirectURI: import.meta.env.VITE_APPLE_REDIRECT_URI ?? window.location.origin,
      // Random state string for CSRF protection
      state:
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : String(Date.now()),
      usePopup: true,
    });
  }, []);

  useEffect(() => {
    // SDK already loaded (e.g. cached by browser)
    if (window.AppleID) {
      initApple();
      return;
    }
    // SDK still loading — listen for the script load event
    const script = document.querySelector<HTMLScriptElement>('script[src*="appleid.auth"]');
    if (!script) return;
    const handler = () => initApple();
    script.addEventListener('load', handler);
    return () => script.removeEventListener('load', handler);
  }, [initApple]);

  const handleAppleSignIn = useCallback(async () => {
    if (!window.AppleID) {
      toast.error('Apple Sign-In is not available. Please refresh the page and try again.');
      return;
    }

    setIsAppleLoading(true);
    try {
      // Triggers the Apple popup — throws if user cancels
      const response = await window.AppleID.auth.signIn();

      const identityToken = response.authorization.id_token;
      const authorizationCode = response.authorization.code;
      // Extract Apple user ID from the JWT sub claim (backend also does this, belt-and-suspenders)
      const userId = getSubFromJwt(identityToken) ?? '';

      // Build payload matching the mobile backend contract exactly
      const payload = {
        identity_token: identityToken,
        authorization_code: authorizationCode,
        user_id: userId,
        // Apple only provides name/email on the FIRST sign-in; subsequent logins return undefined
        email: response.user?.email ?? '',
        given_name: response.user?.name?.firstName ?? '',
        family_name: response.user?.name?.lastName ?? '',
      };

      const data = await appleMutation.mutateAsync(payload);

      // ── Existing user: backend returns { message: "Apple sign-in successful", user: { ..., tokens } }
      const userRaw = (data?.user ?? data?.data ?? data) as ApiUser;
      const tokens =
        userRaw?.tokens ?? (data?.tokens as { access?: string; refresh?: string }) ?? {};
      const authToken = tokens?.access ?? data?.access ?? data?.token ?? data?.access_token;
      const refreshToken = tokens?.refresh ?? data?.refresh ?? data?.refresh_token;

      if (authToken) {
        const mappedUser = mapUserFromAuthResponse(userRaw);
        login(authToken, mappedUser, refreshToken);
        if (mappedUser.profileIncomplete) {
          toast.info('Complete your profile details to unlock all features.');
        }
        const redirectTo =
          typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search).get('redirect')
            : null;
        if (redirectTo && redirectTo !== '/sign-in' && redirectTo.startsWith('/')) {
          window.location.replace(redirectTo);
        } else {
          navigate({ to: '/' });
        }
        return;
      }

      // ── New user: backend returns { user: { ...profile data, no tokens } }
      const userEmail = (userRaw as any)?.email ?? payload.email;
      if (userEmail || userId) {
        signupStore.set({
          flowActive: true,
          email: userEmail ?? '',
          fullName: [payload.given_name, payload.family_name].filter(Boolean).join(' '),
          role: 'creator',
          googleSignedUp: false,
          appleSignedUp: true,
          appleUserId: userId,
        });
        toast.info('Apple account verified. Please complete your registration.');
        navigate({ to: '/personal-information' });
      } else {
        toast.warn('Apple sign-in succeeded but no session was returned. Please try again.');
      }
    } catch (err: any) {
      // User closed/dismissed the Apple popup — do nothing
      const errMsg: string = err?.error ?? err?.message ?? '';
      if (
        errMsg.includes('popup_closed_by_user') ||
        errMsg.toLowerCase().includes('cancel') ||
        err?.error === 'popup_closed_by_user'
      ) {
        return;
      }
      console.error('Apple Sign-In error:', err);
      const isApiError = Boolean(err?.response) || err?.isAxiosError === true;
      // appleMutation.onError already shows a toast for backend-level errors
      if (!isApiError) {
        toast.error('Apple Sign-In failed. Please try again.');
      }
    } finally {
      setIsAppleLoading(false);
    }
  }, [appleMutation, login, navigate, initApple]);

  return { handleAppleSignIn, isAppleLoading };
}
